// =============================================================================
// domain/accountMerge.ts — two copies of ONE account, folded into one.
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html:46440-46489` — semantics, not
// code (D3). `#73`, the last functional item on that audit.
//
// ── 🚨 THE SIGNATURE IS SHARED WITH THE DUPLICATE-TRADE TOOL, DELIBERATELY ──
//
// The old client says why, and it is the sharpest note in that file: the merge
// uses the same key to decide which of the loser's trades are "already on the
// keeper" and can be dropped rather than moved — "so the identical defect could
// destroy a partial fill during an account merge, by a different door. One key,
// one fix." That is why `tradeSignature` is imported here rather than rewritten.
//
// ── 🚨 THE GROUPING IS BY NAME, AND NOTHING ELSE ──
//
// Two accounts are candidates when their names match after lowercasing, dropping
// a `[Sim]` prefix and a `-CME` suffix, and collapsing whitespace. That is a
// weak signal on purpose: it proposes, and a human picks the keeper and reads
// the counts before anything moves.
//
// ⚠ AND IT IS WHY THIS TOOL HAS NO BANNER ON JOSH'S BOOK. His accounts have
// distinct names, so the detector finds nothing — measured 2026-09-04 from his
// own screenshot, where only the duplicate-TRADE banner was showing.
// =============================================================================

import { DEMO_PREFIX } from './demo';
import { tradeSignature, type DupSigTrade } from './duplicateTrades';

/**
 * Beta's `_normAccountName`, verbatim in effect.
 *
 * ⚠ THE TWO STRIPS ARE NOT COSMETIC. `[Sim]` is Sierra Chart's own prefix on a
 * simulated account and `-CME` is an exchange suffix a broker export appends —
 * so "[Sim]Apex 50k" and "Apex 50k" are one account seen through two platforms,
 * which is the entire case this tool exists for.
 */
export function normalizeAccountName(name: string | null | undefined): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/\[sim\]/gi, '')
    .replace(/-cme\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The fields this module reads off an account. */
export interface MergeAccount {
  id: string;
  name: string;
  firm?: string | null;
}

/**
 * Groups of accounts sharing a normalised name.
 *
 * ⚠ AN UNNAMED ACCOUNT IS NEVER A CANDIDATE. Two accounts with blank names
 * normalise to the same empty string and would be offered for merging on the
 * strength of both being unnamed — which is not evidence of anything.
 *
 * ⚠ The demo book is skipped for the same reason it is in the trade detector:
 * its rows are generated, and nothing there is the trader's.
 */
export function detectDuplicateAccounts<T extends MergeAccount>(
  accounts: readonly T[] | null | undefined,
): T[][] {
  const groups = new Map<string, T[]>();
  for (const a of accounts ?? []) {
    const id = String(a?.id ?? '');
    if (!id || id.startsWith(DEMO_PREFIX)) continue;
    const key = normalizeAccountName(a.name);
    if (!key) continue;
    const bucket = groups.get(key);
    if (bucket) bucket.push(a);
    else groups.set(key, [a]);
  }
  return [...groups.values()].filter((g) => g.length > 1);
}

/**
 * Which copy to keep by default.
 *
 * Beta's rule: prefer an account that has a FIRM set, then the one with the most
 * trades. The firm is what carries the rules, the commission schedule and the
 * economics, so a copy without one is almost always the accidental import.
 *
 * ⚠ A DEFAULT, NOT A DECISION. The modal lets the trader change it, and the
 * counts move when they do.
 */
export function defaultMergeKeeper<T extends MergeAccount>(
  group: readonly T[], tradeCountOf: (id: string) => number,
): T | null {
  if (!group.length) return null;
  const withFirm = group.filter((a) => String(a.firm ?? '').trim());
  const pool = withFirm.length ? withFirm : group;
  return pool.slice().sort((a, b) => tradeCountOf(b.id) - tradeCountOf(a.id))[0] ?? null;
}

export interface MergePlan {
  /** Trades that move onto the keeper. */
  repointIds: string[];
  /** Trades already on the keeper in identical form — removed rather than moved. */
  removeIds: string[];
  /** Accounts that will be deleted once their contents have moved. */
  dupeIds: string[];
}

/**
 * What a merge would do. Pure, and it moves nothing.
 *
 * ⚠ A TRADE WITH AN INCOMPLETE SIGNATURE ALWAYS MOVES, never gets dropped.
 * `tradeSignature` returns null when a fill price or size is missing, and the
 * safe reading of "I cannot tell whether this is a duplicate" is to keep it: a
 * moved trade can be deleted later, a deleted one cannot be recovered.
 *
 * ⚠ `seen` GROWS AS THE PLAN IS BUILT, so two identical trades on two different
 * losing copies collapse to one — the second is a duplicate of the first by the
 * time it is reached. Beta does the same, and without it merging three copies of
 * an account would leave two copies of every trade.
 */
export function planAccountMerge(args: {
  keepId: string;
  dupeIds: readonly string[];
  trades: readonly DupSigTrade[];
}): MergePlan {
  const { keepId, dupeIds, trades } = args;

  const seen = new Set<string>();
  for (const t of trades) {
    if (String(t.accountId ?? '') !== keepId) continue;
    const sig = tradeSignature(t);
    if (sig !== null) seen.add(sig);
  }

  const repointIds: string[] = [];
  const removeIds: string[] = [];
  // ⚠ ORDERED BY THE DUPE LIST, not by the trades array, so the plan is the same
  // whatever order the read returned rows in.
  for (const dupeId of dupeIds) {
    for (const t of trades) {
      if (String(t.accountId ?? '') !== String(dupeId)) continue;
      const sig = tradeSignature(t);
      if (sig !== null && seen.has(sig)) { removeIds.push(t.id); continue; }
      if (sig !== null) seen.add(sig);
      repointIds.push(t.id);
    }
  }

  return { repointIds, removeIds, dupeIds: dupeIds.map(String) };
}

/** Trades on one account. The counts the picker and the preview both show. */
export function tradeCountsByAccount(
  trades: readonly DupSigTrade[],
): Map<string, number> {
  const out = new Map<string, number>();
  for (const t of trades) {
    const id = String(t.accountId ?? '');
    if (!id) continue;
    out.set(id, (out.get(id) ?? 0) + 1);
  }
  return out;
}
